import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Button, Avatar, Dropdown, Typography, theme, Space, Badge, Popover, Empty, Tag, Tooltip, Breadcrumb } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, SearchOutlined, BellOutlined, UserOutlined, LogoutOutlined, HomeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useLocation, Link } from 'react-router-dom';
import { useGetIdentity, useLogout } from '@refinedev/core';
import type { AuthUser } from '@/types';
import { CommandPalette, type CommandItem } from '@/shared/components/CommandPalette';

const { Header } = Layout;
const { Text } = Typography;

export interface AppHeaderProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Nav-Items fuer die Command-Palette */
  commands: CommandItem[];
  /** Optionale Notification-Items (z.B. ungeloeste Alerts) */
  notifications?: Array<{
    id: string;
    title: string;
    description?: string;
    time?: string;
    severity?: 'info' | 'warning' | 'critical';
    onClick?: () => void;
  }>;
  /** Label fuer Role-Badge im User-Menu (z.B. "Super-Admin", "Kunde") */
  roleLabel?: string;
  /** Farbakzent (z.B. fuer Role-Badge + Puls-Dot) */
  accent?: string;
  /** Dynamisch berechnete Breadcrumb-Segmente. Wenn leer: automatische Ableitung. */
  breadcrumbs?: Array<{ label: string; to?: string }>;
  /** Pfad-zu-Label Map fuer automatische Breadcrumb-Ableitung */
  pathLabels?: Record<string, string>;
}

function useBreadcrumbs(
  pathname: string,
  overrides: AppHeaderProps['breadcrumbs'],
  labels: AppHeaderProps['pathLabels'],
): Array<{ label: string; to?: string }> {
  return useMemo(() => {
    if (overrides && overrides.length > 0) return overrides;
    const segs = pathname.split('/').filter(Boolean);
    const crumbs: Array<{ label: string; to?: string }> = [];
    let acc = '';
    for (const seg of segs) {
      acc += '/' + seg;
      const label = labels?.[acc] ?? labels?.[seg] ?? seg;
      crumbs.push({ label, to: acc });
    }
    return crumbs;
  }, [pathname, overrides, labels]);
}

export function AppHeader(props: AppHeaderProps): React.ReactElement {
  const { token } = theme.useToken();
  const { pathname } = useLocation();
  const { data: identity } = useGetIdentity<AuthUser>();
  const { mutate: logout } = useLogout();
  const [paletteOpen, setPaletteOpen] = useState(false);

  const breadcrumbs = useBreadcrumbs(pathname, props.breadcrumbs, props.pathLabels);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (isMod && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        props.onToggleCollapsed();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [props]);

  const accent = props.accent ?? token.colorPrimary;
  const emailLetter = (identity?.email ?? '?').slice(0, 1).toUpperCase();

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Abmelden',
      onClick: () => logout(),
    },
  ];

  const notifications = props.notifications ?? [];
  const unreadCount = notifications.length;

  const notifContent = (
    <div style={{ width: 340, maxWidth: '90vw' }}>
      <div
        style={{
          padding: '10px 14px',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text strong>Benachrichtigungen</Text>
        <Tag color={unreadCount > 0 ? 'red' : undefined} style={{ margin: 0 }}>
          {unreadCount}
        </Tag>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {notifications.length === 0 ? (
          <div style={{ padding: 24 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<Text type='secondary'>Keine neuen Benachrichtigungen</Text>}
            />
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              onClick={n.onClick}
              style={{
                padding: '10px 14px',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                cursor: n.onClick ? 'pointer' : 'default',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  marginTop: 7,
                  borderRadius: '50%',
                  background:
                    n.severity === 'critical'
                      ? token.colorError
                      : n.severity === 'warning'
                        ? token.colorWarning
                        : token.colorPrimary,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: token.colorText }}>
                  {n.title}
                </div>
                {n.description && (
                  <div style={{ fontSize: 12, color: token.colorTextSecondary }}>{n.description}</div>
                )}
                {n.time && (
                  <div style={{ fontSize: 11, color: token.colorTextTertiary, marginTop: 2 }}>{n.time}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <>
      <Header
        style={{
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          padding: '0 16px 0 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          height: 64,
          boxShadow: '0 1px 0 rgba(15,23,42,0.02)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
          <Tooltip title={props.collapsed ? 'Navigation anzeigen (⌘B)' : 'Navigation einklappen (⌘B)'}>
            <Button
              type='text'
              onClick={props.onToggleCollapsed}
              icon={props.collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              style={{ fontSize: 16 }}
            />
          </Tooltip>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden' }}>
            <Breadcrumb
              items={[
                {
                  title: (
                    <Link to='/' style={{ color: token.colorTextSecondary }}>
                      <HomeOutlined />
                    </Link>
                  ),
                },
                ...breadcrumbs.map((c, i) => ({
                  title:
                    c.to && i < breadcrumbs.length - 1 ? (
                      <Link to={c.to} style={{ color: token.colorTextSecondary }}>
                        {c.label}
                      </Link>
                    ) : (
                      <span style={{ color: token.colorText, fontWeight: 500 }}>{c.label}</span>
                    ),
                })),
              ]}
              separator='/'
              style={{ fontSize: 13 }}
            />
          </div>
        </div>

        <Space size={6}>
          <Tooltip title='Suchen & Navigieren (⌘K)'>
            <Button
              type='text'
              onClick={() => setPaletteOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                paddingInline: 10,
                borderRadius: 8,
                background: token.colorFillQuaternary,
                border: `1px solid ${token.colorBorderSecondary}`,
                color: token.colorTextSecondary,
                height: 34,
              }}
            >
              <SearchOutlined />
              <span style={{ fontSize: 12 }}>Suchen</span>
              <Tag style={{ margin: 0, border: 'none', background: token.colorFillSecondary, fontSize: 10, padding: '0 6px' }}>
                ⌘K
              </Tag>
            </Button>
          </Tooltip>

          <Popover
            content={notifContent}
            trigger='click'
            placement='bottomRight'
            arrow={false}
            styles={{ body: { padding: 0 } }}
          >
            <Tooltip title='Benachrichtigungen'>
              <Badge count={unreadCount} size='small' offset={[-4, 4]}>
                <Button type='text' icon={<BellOutlined style={{ fontSize: 16 }} />} />
              </Badge>
            </Tooltip>
          </Popover>

          <Tooltip title='Hilfe / Dokumentation'>
            <Button
              type='text'
              icon={<QuestionCircleOutlined style={{ fontSize: 16 }} />}
              onClick={() => window.open('https://docs.schnitzraum.de', '_blank')}
            />
          </Tooltip>

          <Dropdown
            menu={{ items: userMenuItems }}
            placement='bottomRight'
            trigger={['click']}
            dropdownRender={(menu) => (
              <div
                style={{
                  background: token.colorBgElevated,
                  borderRadius: 12,
                  boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
                  minWidth: 240,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: 14,
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <Avatar
                    size={40}
                    style={{
                      background: `linear-gradient(135deg, ${accent}, ${accent}b3)`,
                      fontWeight: 600,
                    }}
                  >
                    {emailLetter}
                  </Avatar>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: token.colorText,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {identity?.email ?? 'Benutzer'}
                    </div>
                    {props.roleLabel && (
                      <Tag
                        style={{
                          marginTop: 2,
                          background: `${accent}1a`,
                          color: accent,
                          border: 'none',
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                        }}
                      >
                        {props.roleLabel}
                      </Tag>
                    )}
                  </div>
                </div>
                {menu}
              </div>
            )}
          >
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 10px 4px 4px',
                borderRadius: 10,
                transition: 'background 0.15s ease',
              }}
            >
              <Avatar
                size={32}
                style={{
                  background: `linear-gradient(135deg, ${accent}, ${accent}b3)`,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {identity ? emailLetter : <UserOutlined />}
              </Avatar>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: token.colorText, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {identity?.email ?? 'Benutzer'}
                </div>
                {props.roleLabel && (
                  <div style={{ fontSize: 10, fontWeight: 600, color: accent, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                    {props.roleLabel}
                  </div>
                )}
              </div>
            </div>
          </Dropdown>
        </Space>
      </Header>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={props.commands} />
    </>
  );
}
